// src/supabaseClient.ts
import { createClient, SupabaseClient } from '@supabase/supabase-js';

// Parse the connection string
type ConnectionString = string;

function parseConnectionString(connStr: ConnectionString) {
  // Example: postgresql://postgres:password@host:port/database
  const regex = /^postgresql:\/\/(.*?):(.*?)@(.*?):(\d+)\/(.*)$/;
  const match = connStr.match(regex);
  if (!match) throw new Error('Invalid connection string');
  return {
    user: match[1],
    password: match[2],
    host: match[3],
    port: match[4],
    database: match[5],
  };
}

// Supabase requires a URL and anon/public key, not a Postgres connection string.
// You must get these from your Supabase project settings.
// For demonstration, we'll use environment variables (recommended for security).

// Support CRA env vars (process.env) and Vite env vars (import.meta.env)
const envAny: any = (typeof import.meta !== 'undefined' && (import.meta as any).env) || process.env || {};
const SUPABASE_URL = (envAny.VITE_REACT_APP_SUPABASE_URL || envAny.REACT_APP_SUPABASE_URL) as string;
const SUPABASE_ANON_KEY = (envAny.VITE_REACT_APP_SUPABASE_ANON_KEY || envAny.REACT_APP_SUPABASE_ANON_KEY) as string;
const SUPABASE_SERVICE_ROLE_KEY = (envAny.VITE_REACT_APP_SUPABASE_SERVICE_ROLE_KEY || envAny.REACT_APP_SUPABASE_SERVICE_ROLE_KEY) as string;

// Global singleton approach to prevent multiple instances
declare global {
  interface Window {
    __SUPABASE_CLIENT__?: SupabaseClient;
    __SUPABASE_ADMIN_CLIENT__?: SupabaseClient;
  }
}

// Function to create or get the main Supabase client (singleton)
function getSupabaseClient(): SupabaseClient {
  // Check if we already have a client in window (for HMR persistence)
  if (typeof window !== 'undefined' && window.__SUPABASE_CLIENT__) {
    console.debug('🔄 Reusing existing Supabase client instance');
    return window.__SUPABASE_CLIENT__;
  }
  
  // Create new client
  console.debug('🆕 Creating new Supabase client instance');
  const client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: 'supabase.auth.token' // Use a consistent storage key
    }
  });
  
  // Store in window for HMR persistence
  if (typeof window !== 'undefined') {
    window.__SUPABASE_CLIENT__ = client;
  }
  
  return client;
}

// Function to create or get the admin Supabase client (singleton)
function getSupabaseAdminClient(): SupabaseClient | null {
  if (!SUPABASE_SERVICE_ROLE_KEY) return null;
  
  // Check if we already have an admin client in window (for HMR persistence)
  if (typeof window !== 'undefined' && window.__SUPABASE_ADMIN_CLIENT__) {
    return window.__SUPABASE_ADMIN_CLIENT__;
  }
  
  // Create new admin client
  const client = createClient(SUPABASE_URL, SUPABASE_SERVICE_ROLE_KEY, {
    auth: {
      autoRefreshToken: false,
      persistSession: false,
      detectSessionInUrl: false,
      storageKey: 'supabase.admin.auth.token' // Use a different storage key for admin
    },
    global: {
      headers: {
        'X-Client-Info': 'supabase-admin-client'
      }
    }
  });
  
  // Store in window for HMR persistence
  if (typeof window !== 'undefined') {
    window.__SUPABASE_ADMIN_CLIENT__ = client;
  }
  
  return client;
}

// Export the main client
export const supabase = getSupabaseClient();

// Export admin client getter function
export const getSupabaseAdmin = (): SupabaseClient | null => {
  return getSupabaseAdminClient();
};

// Only export supabaseAdmin if service role key exists, otherwise export null
export const supabaseAdmin: SupabaseClient | null = SUPABASE_SERVICE_ROLE_KEY ? null : null;

// Note: We don't create the admin client immediately to avoid the multiple instances issue
// Use getSupabaseAdmin() function instead, or the code will need to be updated

// Add a console log to track when this module is evaluated
if (process.env.NODE_ENV === 'development') {
  console.debug('📦 supabaseClient.ts module evaluated at:', new Date().toISOString());
}

// Pre-configured clients for common schemas - using the same instance
export const supabaseReservations = supabase; // Uses public schema but queries reservations.* tables
export const supabasePublic = supabase; // Same as default

// Helper function to get the main client (always returns the singleton)
export const getSupabaseClientInstance = (): SupabaseClient<any, any, any> => {
  return supabase;
};

// Note: For schema-specific operations, use the schema.table syntax in queries
// instead of creating multiple client instances
export default supabase;
